import { useEffect, useState } from "react";
import { TrendingUp, Car, CarFront, Wrench, Fuel, Gauge, Ambulance, } from "lucide-react";
import Skeleton from '@mui/material/Skeleton';
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { Table, TableBody, TableCard, TableCell, TableHead, TableHeaderWrap, TableRow } from "../../../../component/table/table-layout";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { CustomDropdown } from "../../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../../component/ui/dropdown/CustomDropdownMenuList";
import DataNotFound from "../../../../component/ui/loader/data-not-found";
import { Card, CardContent, CardHeader, CardTitle, KPI, TitleHeading } from "../../../../component/ui/layout";

const FleetMySpaceOverview = () => {
  const apiCall = useApiCall();
  const [KPIData, setKPIData] = useState({});
  const [KPILoading, setKPILoading] = useState(false);

  const getKPIData = async () => {
    setKPILoading(false);
    try {
      const resp = await apiCall({ method: "get", url: `fleet-overview/my-summary` });
      console.log("RESP : ", resp)
      setKPIData(resp);
      setTrendData(resp?.fuel_card?.fuel_consumption_trend);
    } catch (error) {
      console.warn("Error : ", error);
    } finally {
      setKPILoading(true);
    }
  }

  useEffect(() => {
    getKPIData();
  }, []);

  const [activeChart, setActiveChart] = useState("Fuel Consumption Trend");
  const [trendData, setTrendData] = useState([]);

  const lineOptions = {
    chart: { type: "line", backgroundColor: "transparent", height: 300 },
    title: { text: "" },
    xAxis: {
      categories:
        activeChart === "Fuel Consumption Trend"
          ? trendData?.map((d) => d.month)
          : (() => {
            const matched = KPIData?.mileage_overview?.find((ele, idx) => {
              const label = `Mileage Trend ${KPIData?.mileage_overview?.length > 1 ? idx + 1 : ""}`;
              return activeChart === label;
            });

            return matched
              ? matched?.mileage_trend?.map((d) => d.month) || []
              : [];
          })(),

      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },
    yAxis: {
      title: { text: activeChart },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },
    series:
      activeChart === "Fuel Consumption Trend"
        ? [
          {
            name: activeChart,
            data: trendData?.map((d) => d.value),
            color: "#2563EB",
          },
        ]
        : (() => {
          const matched = KPIData?.mileage_overview?.find((ele, idx) => {
            const label = `Mileage Trend ${KPIData?.mileage_overview?.length > 1 ? idx + 1 : ""}`;
            return activeChart === label;
          });
          return matched
            ? [
              {
                name: activeChart,
                data: matched.mileage_trend?.map((d) => d.value) || [],
                color: "#2563EB",
              },
            ]
            : [];
        })(),
    legend: { enabled: false },
    credits: { enabled: false },
  };
  
  return (
    <>
      <div className="pt-[20px] overflow-hidden">
        <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
          {/* Header */}
          <div><TitleHeading>Fleets Overview</TitleHeading></div>

          {/* KPI Row */}
          <TableCard className={"!p-[15px] !pr-[7px] h-full overflow-hidden"}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {KPILoading ? (
                KPIData?.car_overviews?.map((val, idx) => {
                  return (
                    <>
                      <KPI
                        icon={Car}
                        label={`My Car ${KPIData?.car_overviews.length > 1 ? idx + 1 : ''}`}
                        value={val?.car_plate || "N/A"}
                        sublabel={`${val?.car_name || "-"} (${val?.car_model || ""})`}
                      />
                      <KPI
                        icon={Wrench}
                        label="Next Maintenance"
                        value={KPIData?.maintenance_alerts?.[idx]?.km_remaining || "-"}
                        sublabel={`Due Date: ${KPIData?.maintenance_alerts?.[idx]?.due_date || "N/A"}`}
                      />
                      <KPI
                        icon={Ambulance}
                        label="Damage Incidents"
                        value={KPIData?.incident_overview?.total_accidents ?? 0}
                        sublabel="Total Accident Reports"
                      />
                      <KPI
                        icon={Gauge}
                        label="Mileage"
                        value={KPIData?.mileage_overview?.[idx]?.last_month_mileage ?? 0}
                        sublabel="Last Month Mileage"
                      />
                      <KPI
                        icon={Fuel}
                        label="Fuel"
                        value={KPIData?.fuel_card?.amount ?? 0}
                        sublabel="Total Fuel Cost"
                      />
                    </>
                  )
                })
              ) : (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="bg-white shadow rounded-xl p-4 animate-pulse">
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
              <Card className="col-span-3 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    {
                      KPIData?.mileage_overview?.length ? (
                        <CustomDropdown position="bottom-right" className="myDropdown !w-[250px]">
                          <CustomDropdownButton arrow={true} className="btnClass !w-[250px] !justify-between">
                            <h3 className="!text-[16px]">{activeChart}</h3>
                          </CustomDropdownButton>

                          <CustomDropdownMenu className="w-full">
                            {/* Fuel Trend */}
                            {activeChart !== "Fuel Consumption Trend" && (
                              <CustomDropdownMenuList onClick={() => setActiveChart("Fuel Consumption Trend")}>
                                <div className="d-flex whitespace-normal align-items-center gap-1">
                                  Fuel Consumption Trend
                                </div>
                              </CustomDropdownMenuList>
                            )}

                            {/* Mileage Trends */}
                            {KPIData?.mileage_overview?.map((ele, idx) => {
                              const label = `Mileage Trend ${KPIData?.mileage_overview?.length > 1 ? idx + 1 : ""}`;
                              return (
                                activeChart !== label && (
                                  <CustomDropdownMenuList key={idx} onClick={() => setActiveChart(label)}>
                                    <div className="d-flex whitespace-normal align-items-center gap-1">{label}</div>
                                  </CustomDropdownMenuList>
                                )
                              );
                            })}
                          </CustomDropdownMenu>
                        </CustomDropdown>
                      ) : activeChart
                    }
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {
                    KPILoading ? (
                      <HighchartsReact highcharts={Highcharts} options={lineOptions} />
                    ) : (
                      <InnerLoader />
                    )
                  }
                </CardContent>
              </Card>

              <Card className="col-span-2 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CarFront className="h-5 w-5" /> Recent Incident
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {
                    KPILoading ? (
                      KPIData?.incident_overview?.recent_incidents?.length ? (
                        <>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell header><TableHeaderWrap>ID</TableHeaderWrap></TableCell>
                                <TableCell header><TableHeaderWrap>Incident Type</TableHeaderWrap></TableCell>
                                <TableCell header><TableHeaderWrap>Car ID</TableHeaderWrap></TableCell>
                                <TableCell header><TableHeaderWrap>Car Plate</TableHeaderWrap></TableCell>
                                <TableCell header><TableHeaderWrap>Car Model</TableHeaderWrap></TableCell>
                                <TableCell header><TableHeaderWrap>Date</TableHeaderWrap></TableCell>
                                <TableCell header><TableHeaderWrap>Severity</TableHeaderWrap></TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {
                                KPIData?.incident_overview?.recent_incidents?.map((data, idx) => {
                                  return (
                                    <TableRow key={`incidet_row_${idx}`}>
                                      <TableCell>{data?.id}</TableCell>
                                      <TableCell>{data?.incident_type}</TableCell>
                                      <TableCell>{data?.car_id}</TableCell>
                                      <TableCell>{data?.car_plate}</TableCell>
                                      <TableCell>{data?.car_model}</TableCell>
                                      <TableCell>{data?.date}</TableCell>
                                      <TableCell>{data?.severity}</TableCell>
                                    </TableRow>
                                  )
                                })
                              }
                            </TableBody>
                          </Table>
                        </>
                      ) : <div className="w-full h-full flex items-center justify-center"><DataNotFound title={'Recent Incident'} /></div>
                    ) : (
                      <InnerLoader />
                    )
                  }
                </CardContent>
              </Card>
            </div>
          </TableCard>
        </div>
      </div>
    </>
  );
}

export default FleetMySpaceOverview
